import React, { useState, useEffect } from 'react';
import { 
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Avatar,
  Chip
} from '@mui/material';
import { 
  TrendingUp, 
  DollarSign, 
  PieChart, 
  BarChart3,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Receipt,
  Building2,
  Clock,
  FileBarChart
} from 'lucide-react';
import { motion } from 'framer-motion';
import AccountingService, { AccountingTransaction } from '../services/accountingService';
import { propertyApi, currencyApi } from '../services/api';
import AccountingTransactions from '../components/AccountingTransactions';
import ChartOfAccountsComponent from '../components/ChartOfAccounts';
import FinancialReports from '../components/FinancialReports';
import PurchaseInvoices from '../components/PurchaseInvoices';

// Local interface for properties used in this component
interface AccountingProperty {
  id: number;
  title: string;
  name: string; // For compatibility with other components
  currency_id?: number;
  currency?: Currency;
  units?: any[];
}

interface Currency {
  id: number;
  name: string;
  symbol: string;
  code: string;
}

const AccountingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [properties, setProperties] = useState<AccountingProperty[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | undefined>();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();
  const [transactions, setTransactions] = useState<AccountingTransaction[]>([]);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Handle property change and reset unit selection
  const handlePropertyChange = (propertyId?: number) => {
    setSelectedPropertyId(propertyId);
    setSelectedUnitId(undefined); // Reset unit when property changes
  };

  // Handle unit change
  const handleUnitChange = (unitId?: number) => {
    setSelectedUnitId(unitId);
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Fallback currencies (used if API fails)
      const fallbackCurrencies = [
        { id: 1, name: 'US Dollar', symbol: '$', code: 'USD', is_default: true },
        { id: 2, name: 'Euro', symbol: '€', code: 'EUR', is_default: false },
        { id: 3, name: 'British Pound', symbol: '£', code: 'GBP', is_default: false }
      ];
      
      // Try to fetch real data from backend
      try {
        console.log('Loading data from backend...');
        
        // Fetch currencies first
        const currenciesResponse = await currencyApi.getAll();
        console.log('Currencies response:', currenciesResponse);
        
        if (currenciesResponse && currenciesResponse.success && Array.isArray(currenciesResponse.data)) {
          setCurrencies(currenciesResponse.data);
          console.log('Currencies loaded from backend:', currenciesResponse.data);
        } else {
          console.warn('Invalid currencies response, using fallback');
          setCurrencies(fallbackCurrencies);
        }
        
        // Fetch properties
        try {
          console.log('Fetching properties from API...');
          const propertiesData = await propertyApi.getAll();
          console.log('Raw properties response:', propertiesData);
          
          if (Array.isArray(propertiesData)) {
            // Transform API properties to AccountingProperty format
            const transformedProperties = propertiesData.map(prop => ({
              id: prop.id,
              title: prop.name || `Property ${prop.id}`,
              name: prop.name || `Property ${prop.id}`,
              currency_id: prop.currency_id,
              currency: prop.currency,
              units: prop.units
            }));
            setProperties(transformedProperties);
            console.log('Properties loaded from backend:', transformedProperties.length, 'properties');
          } else {
            console.warn('Properties response is not an array:', propertiesData);
            setProperties([
              { id: 1, title: 'Downtown Apartments', name: 'Downtown Apartments', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
              { id: 2, title: 'Suburban Villas', name: 'Suburban Villas', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
              { id: 3, title: 'City Center Complex', name: 'City Center Complex', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] }
            ]);
          }
        } catch (propError) {
          console.error('Error loading properties:', propError);
          if (propError && typeof propError === 'object' && 'response' in propError) {
            const axiosError = propError as any;
            console.error('Properties API Error Status:', axiosError.response?.status);
            console.error('Properties API Error Data:', axiosError.response?.data);
          }
          // Use fallback properties if API fails
          setProperties([
            { id: 1, title: 'Downtown Apartments', name: 'Downtown Apartments', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
            { id: 2, title: 'Suburban Villas', name: 'Suburban Villas', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
            { id: 3, title: 'City Center Complex', name: 'City Center Complex', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] }
          ]);
        }
        
      } catch (apiError) {
        console.error('API Error:', apiError);
        // Use fallback data if API completely fails
        setCurrencies(fallbackCurrencies);
        setProperties([
          { id: 1, title: 'Downtown Apartments', name: 'Downtown Apartments', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
          { id: 2, title: 'Suburban Villas', name: 'Suburban Villas', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
          { id: 3, title: 'City Center Complex', name: 'City Center Complex', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] }
        ]);
      }
      
      setDashboardData({
        total_revenue: 125000,
        total_expenses: 85000,
        net_income: 40000,
        pending_transactions: 12,
        monthly_revenue: [
          { month: 'Jan', amount: 20000 },
          { month: 'Feb', amount: 25000 },
          { month: 'Mar', amount: 30000 },
          { month: 'Apr', amount: 28000 },
          { month: 'May', amount: 32000 },
          { month: 'Jun', amount: 35000 }
        ]
      });
    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to mock data if API fails
      setProperties([
        { id: 1, title: 'Downtown Apartments', name: 'Downtown Apartments', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
        { id: 2, title: 'Suburban Villas', name: 'Suburban Villas', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] },
        { id: 3, title: 'City Center Complex', name: 'City Center Complex', currency_id: 1, currency: { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' }, units: [] }
      ]);
      setCurrencies([
        { id: 1, name: 'US Dollar', symbol: '$', code: 'USD' },
        { id: 2, name: 'Euro', symbol: '€', code: 'EUR' },
        { id: 3, name: 'British Pound', symbol: '£', code: 'GBP' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const TransactionCard = ({ transaction }: { transaction: any }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card 
        sx={{ 
          p: 2, 
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
          transition: 'all 0.3s ease'
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  background: 'linear-gradient(135deg, #1976d2, #9c27b0)' 
                }}
              >
                <Receipt size={20} />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {transaction.reference}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {transaction.date}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={transaction.type}
              size="small"
              sx={{
                bgcolor: transaction.type === 'income' ? '#e8f5e8' : '#ffebee',
                color: transaction.type === 'income' ? '#2e7d32' : '#c62828',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {transaction.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: transaction.type === 'income' ? '#2e7d32' : '#c62828'
              }}
            >
              {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toLocaleString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Eye size={16} />
              </Box>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Edit size={16} />
              </Box>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Trash2 size={16} />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const DashboardView = () => (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Text */}
      <Typography variant="h4" component="h2" sx={{ mb: 4, color: 'text.primary', fontWeight: 500 }}>
        Here's what's happening with your properties today
      </Typography>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Properties Card */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 4,
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                border: '1px solid #e3f2fd',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h2" 
                      component="div" 
                      sx={{ 
                        fontSize: '4rem', 
                        fontWeight: 'bold', 
                        color: '#1976d2',
                        mb: 2,
                        lineHeight: 1
                      }}
                    >
                      8
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                      Properties
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Properties
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      managed
                    </Typography>
                  </Box>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: '#1976d2',
                      ml: 2
                    }}
                  >
                    <Building2 size={40} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Total Units Card */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 4,
                background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
                border: '1px solid #fce4ec',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h2" 
                      component="div" 
                      sx={{ 
                        fontSize: '4rem', 
                        fontWeight: 'bold', 
                        color: '#c2185b',
                        mb: 2,
                        lineHeight: 1
                      }}
                    >
                      5
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                      Total Units
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Available units
                    </Typography>
                  </Box>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: '#c2185b',
                      ml: 2
                    }}
                  >
                    <Building2 size={40} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Total Bookings Card */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 4,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                border: '1px solid #e8f5e8',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h2" 
                      component="div" 
                      sx={{ 
                        fontSize: '4rem', 
                        fontWeight: 'bold', 
                        color: '#388e3c',
                        mb: 2,
                        lineHeight: 1
                      }}
                    >
                      0
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                      Bookings
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      All time
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      bookings
                    </Typography>
                  </Box>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: '#388e3c',
                      ml: 2
                    }}
                  >
                    <Calendar size={40} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Active Bookings Card */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 4,
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)',
                border: '1px solid #fff3e0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h2" 
                      component="div" 
                      sx={{ 
                        fontSize: '4rem', 
                        fontWeight: 'bold', 
                        color: '#f57c00',
                        mb: 2,
                        lineHeight: 1
                      }}
                    >
                      0
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}>
                      Active
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                      Bookings
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Currently active
                    </Typography>
                  </Box>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: '#f57c00',
                      ml: 2
                    }}
                  >
                    <TrendingUp size={40} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>Monthly Revenue</Typography>
                <Box sx={{ height: 300, display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 1 }}>
                  {dashboardData?.monthly_revenue?.map((item: any, index: number) => (
                    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <Box
                        sx={{
                          width: '100%',
                          background: 'linear-gradient(to top, #1976d2, #42a5f5)',
                          borderRadius: '4px 4px 0 0',
                          height: `${(item.amount / 35000) * 100}%`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'linear-gradient(to top, #1565c0, #1976d2)',
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                        {item.month}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>Expense Breakdown</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { category: 'Maintenance', amount: 25000, color: '#f44336' },
                    { category: 'Utilities', amount: 20000, color: '#ff9800' },
                    { category: 'Insurance', amount: 15000, color: '#2196f3' },
                    { category: 'Taxes', amount: 12000, color: '#4caf50' },
                    { category: 'Other', amount: 13000, color: '#9c27b0' }
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: item.color 
                          }} 
                        />
                        <Typography variant="body2">{item.category}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        ${item.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card sx={{ p: 3, borderRadius: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Recent Transactions</Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main', 
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                View All
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {[
                { reference: 'TXN-001', date: '2024-06-27', description: 'Rent Payment - Unit 101', amount: 2500, type: 'income' },
                { reference: 'TXN-002', date: '2024-06-26', description: 'Maintenance - Plumbing', amount: 450, type: 'expense' },
                { reference: 'TXN-003', date: '2024-06-25', description: 'Rent Payment - Unit 102', amount: 2800, type: 'income' }
              ].map((transaction, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <TransactionCard transaction={transaction} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </motion.div>
    </Container>
  );

  const TransactionsView = () => (
    <>
      <AccountingTransactions
        properties={properties}
        currencies={currencies}
        selectedPropertyId={selectedPropertyId}
        onPropertyChange={handlePropertyChange}
        selectedUnitId={selectedUnitId}
        onUnitChange={handleUnitChange}
      />
    </>
  );

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: '#f5f5f5', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={64} sx={{ mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Loading accounting data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #9c27b0 50%, #3f51b5 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Accounting
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Manage your financial transactions and analytics
                </Typography>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                <Paper 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    px: 2, 
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Building2 size={20} />
                  <Typography variant="body2">All Properties</Typography>
                </Paper>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Navigation Tabs */}
      <Container maxWidth="xl" sx={{ position: 'relative', mt: -2 }}>
        <Paper 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.95)', 
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            overflow: 'hidden'
          }}
        >
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ 
              p: 2,
              '& .MuiTabs-indicator': {
                display: 'none'
              }
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              value="dashboard" 
              icon={<BarChart3 size={22} />} 
              label="DASHBOARD" 
              iconPosition="start"
              sx={{ 
                borderRadius: 3,
                mr: 2,
                minHeight: 56,
                px: 4,
                py: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: activeTab === 'dashboard' ? 'white !important' : 'text.secondary',
                '& .MuiTab-iconWrapper': {
                  color: activeTab === 'dashboard' ? 'white !important' : 'inherit'
                },
                background: activeTab === 'dashboard' 
                  ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                  : 'transparent',
                boxShadow: activeTab === 'dashboard' 
                  ? '0 4px 20px rgba(79, 107, 255, 0.4)' 
                  : 'none',
                '&:hover': {
                  background: activeTab === 'dashboard' 
                    ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                    : 'rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                gap: 1.5
              }}
            />
            <Tab 
              value="transactions" 
              icon={<DollarSign size={22} />} 
              label="TRANSACTIONS" 
              iconPosition="start"
              sx={{ 
                borderRadius: 3,
                mr: 2,
                minHeight: 56,
                px: 4,
                py: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: activeTab === 'transactions' ? 'white !important' : 'text.secondary',
                '& .MuiTab-iconWrapper': {
                  color: activeTab === 'transactions' ? 'white !important' : 'inherit'
                },
                background: activeTab === 'transactions' 
                  ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                  : 'transparent',
                boxShadow: activeTab === 'transactions' 
                  ? '0 4px 20px rgba(79, 107, 255, 0.4)' 
                  : 'none',
                '&:hover': {
                  background: activeTab === 'transactions' 
                    ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                    : 'rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                gap: 1.5
              }}
            />
            <Tab 
              value="purchase-invoices" 
              icon={<Receipt size={22} />} 
              label="PURCHASE INVOICES" 
              iconPosition="start"
              sx={{ 
                borderRadius: 3,
                mr: 2,
                minHeight: 56,
                px: 4,
                py: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: activeTab === 'purchase-invoices' ? 'white !important' : 'text.secondary',
                '& .MuiTab-iconWrapper': {
                  color: activeTab === 'purchase-invoices' ? 'white !important' : 'inherit'
                },
                background: activeTab === 'purchase-invoices' 
                  ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                  : 'transparent',
                boxShadow: activeTab === 'purchase-invoices' 
                  ? '0 4px 20px rgba(79, 107, 255, 0.4)' 
                  : 'none',
                '&:hover': {
                  background: activeTab === 'purchase-invoices' 
                    ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                    : 'rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                gap: 1.5
              }}
            />
            <Tab 
              value="accounts" 
              icon={<Clock size={22} />} 
              label="CHART OF ACCOUNTS" 
              iconPosition="start"
              sx={{ 
                borderRadius: 3,
                mr: 2,
                minHeight: 56,
                px: 4,
                py: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: activeTab === 'accounts' ? 'white !important' : 'text.secondary',
                '& .MuiTab-iconWrapper': {
                  color: activeTab === 'accounts' ? 'white !important' : 'inherit'
                },
                background: activeTab === 'accounts' 
                  ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                  : 'transparent',
                boxShadow: activeTab === 'accounts' 
                  ? '0 4px 20px rgba(79, 107, 255, 0.4)' 
                  : 'none',
                '&:hover': {
                  background: activeTab === 'accounts' 
                    ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                    : 'rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                gap: 1.5
              }}
            />
            <Tab 
              value="reports" 
              icon={<FileBarChart size={22} />} 
              label="REPORTS" 
              iconPosition="start"
              sx={{ 
                borderRadius: 3,
                minHeight: 56,
                px: 4,
                py: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: activeTab === 'reports' ? 'white !important' : 'text.secondary',
                '& .MuiTab-iconWrapper': {
                  color: activeTab === 'reports' ? 'white !important' : 'inherit'
                },
                background: activeTab === 'reports' 
                  ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                  : 'transparent',
                boxShadow: activeTab === 'reports' 
                  ? '0 4px 20px rgba(79, 107, 255, 0.4)' 
                  : 'none',
                '&:hover': {
                  background: activeTab === 'reports' 
                    ? 'linear-gradient(135deg, #4f6bff, #8b5cf6)' 
                    : 'rgba(0,0,0,0.04)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                gap: 1.5
              }}
            />
          </Tabs>
        </Paper>
      </Container>

      {/* Content */}
      <Box sx={{ py: 4 }}>
        {activeTab === 'dashboard' && <DashboardView />}
        {activeTab === 'transactions' && <TransactionsView />}
        {activeTab === 'purchase-invoices' && (
          <Container maxWidth="xl">
            <PurchaseInvoices
              properties={properties}
              selectedPropertyId={selectedPropertyId}
              selectedUnitId={selectedUnitId}
            />
          </Container>
        )}
        {activeTab === 'accounts' && (
          <ChartOfAccountsComponent
            properties={properties}
            selectedPropertyId={selectedPropertyId}
            onPropertyChange={handlePropertyChange}
          />
        )}
        {activeTab === 'reports' && (
          <FinancialReports
            properties={properties}
            currencies={currencies}
            selectedPropertyId={selectedPropertyId}
            onPropertyChange={handlePropertyChange}
            selectedUnitId={selectedUnitId}
            onUnitChange={handleUnitChange}
          />
        )}
      </Box>
    </Box>
  );
};

export default AccountingPage;
